// src/services/websiteTimeTracker.js
// Tracks total time spent on the website (any activity, not just study sessions)
import { db, auth } from '../firebase';
import { collection, addDoc, query, where, getDocs, Timestamp, serverTimestamp } from 'firebase/firestore';

class WebsiteTimeTracker {
  constructor() {
    this.sessionStartTime = null;
    this.trackingInterval = null;
    this.lastActivityTime = Date.now();
    this.isTracking = false;
    this.inactiveThreshold = 5 * 60 * 1000; // 5 minutes of inactivity
    this.saveInterval = 2 * 60 * 1000; // Save every 2 minutes
    // Which parts of the product were opened during the current session.
    this.visitedPaths = new Set();
  }

  /**
   * Note that the signed-in user opened `path`.
   *
   * Saved with the session so we can show which features an account actually
   * used, not just how long it was open. This is the evidence a payment
   * provider asks for when a buyer claims they never authorized a purchase —
   * for a digital product there is no delivery receipt, so proof of access IS
   * the proof of delivery.
   */
  recordPath(path) {
    if (!this.isTracking || typeof path !== 'string' || !path) return;
    // Strip the /:lang prefix so paths aggregate across languages, and cap the
    // set so a long session can't grow the document without bound.
    const normalized = path.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';
    if (this.visitedPaths.size < 100) this.visitedPaths.add(normalized);
  }

  /**
   * Start tracking time spent on website
   */
  startTracking() {
    const user = auth.currentUser;
    if (!user || this.isTracking) return;

    console.log('Starting website time tracking');
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.isTracking = true;
    this.visitedPaths.clear();

    // Track activity events
    this.setupActivityListeners();

    // Periodically save accumulated time
    this.trackingInterval = setInterval(() => {
      this.saveAccumulatedTime();
    }, this.saveInterval);

    // Handle page visibility (user switches tabs, minimizes window, etc.)
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    window.addEventListener('beforeunload', this.handlePageUnload.bind(this));
  }

  /**
   * Stop tracking time
   */
  stopTracking() {
    if (!this.isTracking) return;

    console.log('Stopping website time tracking');
    this.saveAccumulatedTime(true); // Final save
    this.isTracking = false;
    this.sessionStartTime = null;

    if (this.trackingInterval) {
      clearInterval(this.trackingInterval);
      this.trackingInterval = null;
    }

    // Remove event listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.handlePageUnload);
  }

  /**
   * Setup activity listeners to detect user interaction
   */
  setupActivityListeners() {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const updateActivity = () => {
      this.lastActivityTime = Date.now();
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });
  }

  /**
   * Handle page visibility changes (tab switch, minimize, etc.)
   */
  handleVisibilityChange() {
    if (document.hidden) {
      // Page is hidden - save current time and pause tracking
      this.saveAccumulatedTime();
    } else {
      // Page is visible again - resume tracking
      this.lastActivityTime = Date.now();
      if (this.sessionStartTime) {
        // Adjust session start time to account for hidden period
        const hiddenDuration = Date.now() - this.lastActivityTime;
        this.sessionStartTime += hiddenDuration;
      } else {
        this.sessionStartTime = Date.now();
      }
    }
  }

  /**
   * Handle page unload (user closes tab/window)
   */
  handlePageUnload() {
    this.saveAccumulatedTime(true);
  }

  /**
   * Calculate active time (excluding inactive periods)
   */
  calculateActiveTime() {
    if (!this.sessionStartTime) return 0;

    const now = Date.now();
    const inactiveTime = now - this.lastActivityTime;

    // If user has been inactive for more than threshold, don't count that time
    if (inactiveTime > this.inactiveThreshold) {
      // Only count time up to when they became inactive
      return Math.max(0, this.lastActivityTime - this.sessionStartTime);
    }

    // User is active, count all time
    return now - this.sessionStartTime;
  }

  /**
   * Save accumulated time to Firestore
   */
  async saveAccumulatedTime(isFinal = false) {
    const user = auth.currentUser;
    if (!user || !this.sessionStartTime) return;

    const activeTime = this.calculateActiveTime();
    // Sessions shorter than a minute used to be discarded entirely, which left
    // short but real visits with no record at all. `duration` stays in whole
    // minutes because the dashboard sums it; `durationSeconds` is what makes a
    // brief visit provable.
    if (activeTime < 5 * 1000) return;

    const minutes = Math.floor(activeTime / (60 * 1000));

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Create a time tracking entry
      await addDoc(collection(db, 'users', user.uid, 'websiteSessions'), {
        userId: user.uid,
        startTime: Timestamp.fromDate(new Date(this.sessionStartTime)),
        endTime: serverTimestamp(),
        duration: minutes, // Duration in minutes
        durationSeconds: Math.round(activeTime / 1000),
        date: Timestamp.fromDate(today), // Date for easy querying
        isActive: !isFinal, // Whether this session is still ongoing
        paths: Array.from(this.visitedPaths),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        createdAt: serverTimestamp()
      });

      // Reset session start time for next interval
      if (!isFinal) {
        this.sessionStartTime = Date.now();
        this.lastActivityTime = Date.now();
      }
      this.visitedPaths.clear();

      console.log(`Saved ${minutes} minutes of website time`);
    } catch (error) {
      console.error('Error saving website time:', error);
    }
  }

  /**
   * Get total time spent today (in minutes)
   */
  async getTodayTime() {
    const user = auth.currentUser;
    if (!user) return 0;

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const sessionsRef = collection(db, 'users', user.uid, 'websiteSessions');
      const q = query(
        sessionsRef,
        where('date', '>=', Timestamp.fromDate(today))
      );

      const querySnapshot = await getDocs(q);
      let totalMinutes = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        totalMinutes += data.duration || 0;
      });

      // Add current session time if tracking
      if (this.isTracking && this.sessionStartTime) {
        const currentMinutes = Math.floor(this.calculateActiveTime() / (60 * 1000));
        totalMinutes += currentMinutes;
      }

      return totalMinutes;
    } catch (error) {
      console.error('Error getting today time:', error);
      return 0;
    }
  }

  /**
   * Get time spent for a specific date range
   */
  async getTimeForDateRange(startDate, endDate) {
    const user = auth.currentUser;
    if (!user) return {};

    try {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const sessionsRef = collection(db, 'users', user.uid, 'websiteSessions');
      const q = query(
        sessionsRef,
        where('date', '>=', Timestamp.fromDate(start)),
        where('date', '<=', Timestamp.fromDate(end))
      );

      const querySnapshot = await getDocs(q);
      const timeByDate = {};

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const date = data.date?.toDate() || new Date();
        const dateStr = date.toISOString().split('T')[0];

        if (!timeByDate[dateStr]) {
          timeByDate[dateStr] = 0;
        }
        timeByDate[dateStr] += data.duration || 0;
      });

      return timeByDate;
    } catch (error) {
      console.error('Error getting time for date range:', error);
      return {};
    }
  }
}

export default new WebsiteTimeTracker();
